import { useState, useCallback } from 'react'
import { Head } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput } from '@/components/ui/form'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { PencilIcon, TrashIcon, TruckIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface CarrierRow { id: number; name: string }
interface CarriersIndexProps { carriers: CarrierRow[] }

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function CarriersIndex({ carriers: initialCarriers }: CarriersIndexProps) {
  const [carriers, setCarriers] = useState(initialCarriers)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<CarrierRow | null>(null)
  const [formName, setFormName] = useState('')
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow, flash } = useFlash()

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(carriers)

  // ─── CRUD ───────────────────────────────────
  const openCreate = () => { setEditing(null); setFormName(''); setFormErrors([]); setDialogOpen(true) }
  const openEdit = (c: CarrierRow) => { setEditing(c); setFormName(c.name); setFormErrors([]); setDialogOpen(true) }

  const handleSubmit = async () => {
    if (!formName.trim()) { setFormErrors(['Name is required']); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const url = editing ? `/inertia_carriers/${editing.id}.json` : '/inertia_carriers.json'
      const method = editing ? 'PATCH' : 'POST'
      const res = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ carrier: { name: formName.trim() } }),
      })
      if (res.ok) {
        const data = await res.json()
        if (editing) {
          setCarriers(prev => prev.map(c => c.id === data.id ? data : c))
          flash(data.id, 'success')
        } else {
          setCarriers(prev => [data, ...prev])
        }
        setDialogOpen(false)
        notify(editing ? 'Saved:Carrier updated' : 'Saved:Carrier created')
      } else {
        const err = await res.json().catch(() => ({}))
        setFormErrors(err.errors || ['Failed to save'])
      }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deleteCarrier = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this carrier?')) return
    try {
      const res = await fetch(`/inertia_carriers/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setCarriers(prev => prev.filter(c => c.id !== id)); notify('Deleted:Carrier removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<CarrierRow>[] = [
    {
      key: 'name', label: 'Carrier Name',
      render: row => <span className="font-medium">{row.name}</span>,
      sortValue: row => row.name,
      filterValue: row => row.name,
    },
  ]

  return (
    <>
      <Head title="Carriers" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />

      <PageHeader title="Carriers" icon={<TruckIcon className="h-5 w-5" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={openCreate}>+ New Carrier</Button>
        </div>
      } />

      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} carriers</span></Badge></div>

      <DataTable
        rows={paginatedRows} columns={columns} rowKey={row => row.id}
        flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null}
        emptyText="No carriers" filteredEmptyText="No carriers match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <button onClick={() => openEdit(row)} className="rounded p-1 text-zinc-400 hover:text-indigo-600 dark:text-zinc-500 dark:hover:text-indigo-400" title="Edit"><PencilIcon className="h-4 w-4" /></button>
            <button onClick={() => deleteCarrier(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{editing ? 'Edit Carrier' : 'New Carrier'}</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Name *</label>
            <FormInput value={formName} onChange={e => setFormName((e.target as HTMLInputElement).value)} placeholder="Carrier name..." autoFocus />
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={submitting} onClick={handleSubmit}>{submitting ? 'Saving...' : (editing ? 'Save' : 'Create')}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

CarriersIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
